import React from 'react';
import { motion } from 'framer-motion';
import { Gift, Check, ArrowRight, Filter } from 'lucide-react';
import { useCurrency } from '../utils/currency';
export function BonusOneSection() {
  const {
    formatPrice
  } = useCurrency();
  const includes = ['Las 3 preguntas exactas que separan compradores de curiosos', 'Mini árbol de decisión (qué responder según cada tipo de prospecto)', '8 scripts copiables para filtrar sin sonar frío', 'Checklist de 5 pasos (anti-tiempo perdido)', 'Versión corta (3 preguntas) + versión larga (5 preguntas)'];
  const bullets = ['"¿Lo querés para esta semana o más adelante?" (la pregunta que te dice TODO)', 'Cómo detectar al que pide info para comparar sin decírtelo', 'Qué hacer cuando alguien dice "lo estoy viendo" (no dejarlo ir)', 'La frase exacta para responder cuando piden precio demasiado pronto'];
  return <section className="w-full bg-white py-20 px-4 sm:px-6 lg:px-8 pt-[40px] pb-[40px]" data-id="element-106">
      <div className="max-w-6xl mx-auto" data-id="element-107">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start" data-id="element-108">
          {/* Image — LEFT */}
          <motion.div initial={{
          opacity: 0,
          x: -24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="flex justify-center lg:justify-start" data-id="element-109">
            <div className="relative max-w-xs w-full" data-id="element-110">
              <img src="https://cdn.magicpatterns.com/uploads/cpkCrNZBZeAirrXCMxMwHJ/bono_1_diagnostico_flash.png" alt="Bono 1: Diagnóstico Flash en 3 Preguntas" className="w-full h-auto object-contain drop-shadow-xl rounded-lg" data-id="element-111" />
              {/* Value badge */}
              <div className="absolute -top-3 -right-3 bg-[#0B6B3A] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg" data-id="element-112">
                Valor: {formatPrice(17)}
              </div>
            </div>
          </motion.div>

          {/* Text — RIGHT */}
          <motion.div initial={{
          opacity: 0,
          x: 24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="flex flex-col gap-5" data-id="element-113">
            {/* Badge */}
            <div className="flex items-center gap-2" data-id="element-114">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0B6B3A] text-white text-sm font-bold rounded-full" data-id="element-115">
                <Gift className="w-4 h-4" data-id="element-116" />
                BONO #1 — GRATIS
              </span>
              <span className="text-sm text-[#6B7280] line-through" data-id="element-117">
                {formatPrice(17)}
              </span>
            </div>

            {/* Title */}
            <div data-id="element-118">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] leading-tight mb-2" data-id="element-119">
                Diagnóstico Flash en 3 Preguntas
              </h2>
              <p className="text-lg text-[#6B7280] font-medium flex items-center gap-2" data-id="element-120">
                <Filter className="w-4 h-4 text-[#0B6B3A]" data-id="element-121" />
                Filtro anti-curiosos para vender por DM/WhatsApp
              </p>
            </div>

            {/* Hook */}
            <p className="text-base text-[#1A1A1A] leading-relaxed" data-id="element-122">
              Antes de cerrar, necesitás saber con quién estás hablando. Porque
              si respondés igual a todos, vas a gastar tiempo y energía en
              personas que nunca iban a comprar. El Diagnóstico Flash te da{' '}
              <strong data-id="element-123">3 preguntas exactas</strong> para saber en 60 segundos si
              ese contacto es un comprador o un curioso.
            </p>

            {/* Problem scenario */}
            <div className="bg-[#F9FAFB] border-l-4 border-[#0B6B3A] rounded-r-xl p-5" data-id="element-124">
              <p className="text-sm font-bold text-[#1A1A1A] mb-2 uppercase tracking-wide" data-id="element-125">
                Te ha pasado esto:
              </p>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-126">
                Alguien te escribe "¿tenés info de X?" Vos explicás todo. Le
                mandás precios. Le respondés cada pregunta. Le dedicás 20
                minutos. Y al final… silencio. No era un comprador. Era alguien
                mirando.{' '}
                <strong className="text-[#1A1A1A]" data-id="element-127">
                  Ese tiempo no vuelve.
                </strong>
              </p>
            </div>

            {/* Includes */}
            <div data-id="element-128">
              <p className="text-sm font-bold text-[#1A1A1A] uppercase tracking-wide mb-3" data-id="element-129">
                Lo que incluye:
              </p>
              <ul className="space-y-2" data-id="element-130">
                {includes.map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: 10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.07
              }} className="flex items-start gap-2.5 text-sm text-[#1A1A1A]" data-id="element-131">
                    <Check className="w-4 h-4 text-[#0B6B3A] mt-0.5 flex-shrink-0" data-id="element-132" />
                    {item}
                  </motion.li>)}
              </ul>
            </div>

            {/* Value justification */}
            <p className="text-sm text-[#6B7280] italic border-t border-gray-100 pt-4" data-id="element-133">
              Un solo chat bien filtrado puede ahorrarte 45 minutos de
              conversación vacía. Si cerrás 1 venta extra por semana por filtrar
              mejor, ese bono se paga solo en el primer día.
            </p>

            {/* Bullets */}
            <ul className="space-y-1.5" data-id="element-134">
              {bullets.map((b, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#6B7280]" data-id="element-135">
                  <ArrowRight className="w-3.5 h-3.5 text-[#0B6B3A] mt-0.5 flex-shrink-0" data-id="element-136" />
                  {b}
                </li>)}
            </ul>

            <p className="text-xs text-[#6B7280] font-medium" data-id="element-137">
              Formato: mini-guía PDF + scripts copiables. Acceso inmediato.
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}